import { Badge, Button, Card, EmptyState, MetricCard } from "@qualtio-workhub/ui";
import { useRouter } from "@tanstack/react-router";
import { Bot, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { listIssues } from "../../shared/api/client";
import { useWorkhubQuery } from "../../shared/api/hooks";
import { useAuth } from "../../shared/auth/auth-provider";
import { QueryFeedback } from "../../shared/components/query-feedback";
import { useI18n } from "../../shared/i18n/provider";
import { CreateIssueModal } from "./create-issue-modal";
import { IssueTable } from "./issue-widgets";
import { openIssueCount, sortByUpdatedAt } from "./model";

export function HomePage() {
  const auth = useAuth();
  const router = useRouter();
  const { t } = useI18n();
  const [createOpen, setCreateOpen] = useState(false);
  const issuesQuery = useWorkhubQuery(listIssues);
  const issues = useMemo(
    () =>
      sortByUpdatedAt(
        (issuesQuery.data ?? []).filter(
          (issue) =>
            issue.assigneeUserId === auth.user?.id ||
            issue.creatorUserId === auth.user?.id ||
            issue.assigneeAgentId !== null,
        ),
      ),
    [auth.user, issuesQuery.data],
  );
  const myOpen = openIssueCount(issues);
  const triageCount = issues.filter((issue) => issue.status === "triage").length;
  const agentCount = issues.filter((issue) => issue.assigneeAgentId !== null).length;
  const blockedCount = issues.filter((issue) => issue.status === "blocked").length;

  return (
    <section className="page-frame work-page home-work-page">
      <div className="page-heading">
        <div>
          <Badge tone="accent">{t("work.meta")}</Badge>
          <h1>{t("work.home.title")}</h1>
          <p className="muted-copy">{t("work.home.subtitle")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus size={16} />
          {t("issue.create.submit")}
        </Button>
      </div>
      <div className="metric-row">
        <MetricCard label={t("work.metric.open")} value={myOpen} />
        <MetricCard label={t("work.metric.triage")} tone="accent" value={triageCount} />
        <MetricCard label={t("work.metric.agent")} tone="agent" value={agentCount} />
        <MetricCard
          label={t("work.metric.blocked")}
          tone={blockedCount > 0 ? "danger" : undefined}
          value={blockedCount}
        />
      </div>
      <div className="work-grid">
        <Card className="work-panel wide-panel">
          <PanelTitle title={t("work.home.priority")} />
          {!issuesQuery.error && issues.length > 0 ? (
            <IssueTable compact issues={issues.slice(0, 8)} />
          ) : (
            <QueryFeedback
              loading={issuesQuery.loading}
              error={issuesQuery.error}
              onRetry={issuesQuery.refetch}
              emptyDescription={t("work.empty.desc")}
              emptyTitle={t("work.empty")}
              emptyAction={
                <Button size="sm" variant="secondary" onClick={() => setCreateOpen(true)}>
                  {t("issue.create.submit")}
                </Button>
              }
            />
          )}
        </Card>
        <Card className="work-panel">
          <PanelTitle title={t("work.home.agentQueue")} />
          <div className="side-list">
            {issues
              .filter((issue) => issue.assigneeAgentId !== null)
              .slice(0, 5)
              .map((issue) => (
                <button
                  className="side-list-item"
                  key={issue.id}
                  type="button"
                  onClick={() =>
                    router.navigate({ to: "/issues/$issueKey", params: { issueKey: issue.key } })
                  }
                >
                  <span className="issue-key">{issue.key}</span>
                  <span>{issue.title}</span>
                </button>
              ))}
            {agentCount === 0 && !issuesQuery.error && !issuesQuery.loading ? (
              <EmptyState icon={<Bot size={18} />} title={t("work.home.agentQueueEmpty")} />
            ) : null}
          </div>
        </Card>
      </div>
      <CreateIssueModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(issueKey) => {
          void issuesQuery.refetch();
          void router.navigate({ to: "/issues/$issueKey", params: { issueKey } });
        }}
      />
    </section>
  );
}

function PanelTitle({ title }: { title: string }) {
  return <h2 className="panel-title">{title}</h2>;
}
